import { useMemo } from 'react';
import { map } from 'rxjs/operators';
import { useObservableValue } from '../../hooks/useObservable';
import { wsScanToDoubleMap } from '../../pipes';
import { SecurityTypesUtils, type SecurityTypes } from '../../types';
import type { Security } from '../../types/Security';
import { shouldDeleteSecurityFromIndex, type SecurityMemoryObservable } from './utils';

export function useSpotIndexes(securityMemoryObs: SecurityMemoryObservable) {
  const spotTradingPairsByBaseCurrencyObs = useMemo(
    () =>
      securityMemoryObs.pipe(
        map(({ latestMessage }) => latestMessage),
        wsScanToDoubleMap<Security, string, string, SecurityTypes['Spot'], SecurityTypes['Spot']>({
          getKey1: security => security.BaseCurrency ?? '',
          getKey2: security => security.Symbol,
          getInsertable: security => security,
          newOuterMapEachUpdate: true,
          newInnerMapsEachUpdate: true,
          isRelevant: SecurityTypesUtils.Spot.isType,
          shouldDelete: shouldDeleteSecurityFromIndex,
          deleteInnerMapOnEmptied: true,
        })
      ),
    [securityMemoryObs]
  );
  const spotTradingPairsByQuoteCurrencyObs = useMemo(
    () =>
      securityMemoryObs.pipe(
        map(({ latestMessage }) => latestMessage),
        wsScanToDoubleMap<Security, string, string, SecurityTypes['Spot'], SecurityTypes['Spot']>({
          getKey1: security => security.QuoteCurrency,
          getKey2: security => security.Symbol,
          getInsertable: security => security,
          newOuterMapEachUpdate: true,
          newInnerMapsEachUpdate: true,
          isRelevant: SecurityTypesUtils.Spot.isType,
          shouldDelete: shouldDeleteSecurityFromIndex,
          deleteInnerMapOnEmptied: true,
        })
      ),
    [securityMemoryObs]
  );
  const spotTradingPairsByBaseCurrency = useObservableValue(
    () => spotTradingPairsByBaseCurrencyObs,
    [spotTradingPairsByBaseCurrencyObs],
    new Map()
  );
  const spotTradingPairsByQuoteCurrency = useObservableValue(
    () => spotTradingPairsByQuoteCurrencyObs,
    [spotTradingPairsByQuoteCurrencyObs],
    new Map()
  );

  return useMemo(
    () => ({ spotTradingPairsByBaseCurrency, spotTradingPairsByQuoteCurrency }),
    [spotTradingPairsByBaseCurrency, spotTradingPairsByQuoteCurrency]
  );
}
